import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import Select from 'react-select';
import classNames from 'classnames';
import styles from './GraphDateSelectors.module.scss';
import { Text } from '../../../components/typography';
import {
  format,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  startOfMonth,
} from 'date-fns';
import { options } from '../../../utilities/analytics';

const menuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
};

const toYmd = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'yyyy-MM-dd');
};

const getDateString = (date) => format(date, 'yyyy-MM-dd');

// Builds an unambiguous UTC ISO instant for local midnight/end-of-day of the
// given Date — always via explicit local Y/M/D components (never a naive
// "YYYY-MM-DD HH:mm:ss" string), so it can't be misparsed as UTC downstream.
const localDayBoundaryIso = (date, endOfDay = false) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    0,
  ).toISOString();

// Parses a bare "YYYY-MM-DD" (e.g. from <input type="date">) as a local
// date — native `new Date("YYYY-MM-DD")` parses as UTC per spec, which is
// the wrong mode here.
const ymdStringToLocalDate = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const today = new Date();
const endOfYesterday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() - 1,
  23,
  59,
  59,
  999,
);

export default function GraphDateSelectors({
  setSubheader = () => {},
  setDates = () => {},
  reset = false,
  setReset = () => {},
  setDateType = () => {},
  textProps = {},
  showTitle = true,
  className,
  buttonView = false,
  selected = null,
  initialFrom = null,
  initialTo = null,
  buttonOptions = [],
}) {
  const now = buttonView ? endOfYesterday : today;

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const getBaseDate = (fromVal, toVal) => {
    const base = new Date(fromVal || toVal || now);
    return Number.isNaN(base.getTime()) ? now : base;
  };

  const [selectedMonth, setSelectedMonth] = useState(() =>
    getBaseDate(initialFrom, initialTo).getMonth(),
  );
  const [selectedYear, setSelectedYear] = useState(() =>
    getBaseDate(initialFrom, initialTo).getFullYear(),
  );
  const [customDays, setCustomDays] = useState('');
  const [customRange, setCustomRange] = useState({
    start: toYmd(initialFrom),
    end: toYmd(initialTo),
  });

  const customRef = useRef(null);
  const lastEmittedDatesRef = useRef({ start: null, end: null });

  const [rangeType, setRangeType] = useState(() => {
    const sel = selected == null ? null : String(selected);
    if (sel) {
      const found =
        buttonOptions.find((o) => String(o.value) === sel) ||
        options.find((o) => String(o.value) === sel);
      if (found) return found;
    }
    return options[0];
  });

  const years = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        value: currentYear - i,
        label: `${currentYear - i}`,
      })),
    [currentYear],
  );

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: new Date(0, i).toLocaleString('default', { month: 'long' }),
    })).filter(
      (month) => selectedYear < currentYear || month.value <= currentMonth,
    );
  }, [selectedYear, currentYear, currentMonth]);

  const emitDates = useCallback(
    ({ start, end, subheader }) => {
      const last = lastEmittedDatesRef.current;
      if (last.start === start && last.end === end) return;

      lastEmittedDatesRef.current = { start, end };

      setDates({ start, end });
      if (subheader != null) setSubheader(subheader);
    },
    [setDates, setSubheader],
  );

  const applyRange = useCallback(() => {
    const v = String(rangeType?.value ?? '');
    if (v === 'custom' || v === 'from') return;
    if (v === 'custom_days') {
      const n = parseInt(customDays, 10);
      if (!Number.isFinite(n) || n <= 0) return;

      const startDate = subDays(today, n);
      const start = localDayBoundaryIso(startDate);
      const end = localDayBoundaryIso(now, true);
      emitDates({
        start,
        end,
        subheader: `${getDateString(startDate)} - ${getDateString(now)}`,
      });
      setCustomRange({
        start: getDateString(startDate),
        end: getDateString(now),
      });
      return;
    }
    if (v === 'ytd') {
      const endDate = now;
      const startDate = new Date(endDate.getFullYear(), 0, 1);
      const start = localDayBoundaryIso(startDate);
      const end = localDayBoundaryIso(endDate, true);
      emitDates({
        start,
        end,
        subheader: `${getDateString(startDate)} - ${getDateString(endDate)}`,
      });
      setCustomRange({
        start: getDateString(startDate),
        end: getDateString(endDate),
      });
      return;
    }
    const asNumber = parseInt(v, 10);
    if (!Number.isNaN(asNumber)) {
      const startDate = subDays(today, asNumber);
      const startYmd = getDateString(startDate);
      const endYmd = getDateString(now);

      setCustomRange({ start: startYmd, end: endYmd });
      emitDates({
        start: localDayBoundaryIso(startDate),
        end: localDayBoundaryIso(now, true),
        subheader: `${startYmd} to ${endYmd}`,
      });
      return;
    }
    if (v === 'monthly') {
      const d = new Date(selectedYear, selectedMonth, 1);
      const startDate = startOfMonth(d);
      const endDate = endOfMonth(d);
      const startYmd = getDateString(startDate);
      const endYmd = getDateString(endDate);

      setCustomRange({ start: startYmd, end: endYmd });
      emitDates({
        start: localDayBoundaryIso(startDate),
        end: localDayBoundaryIso(endDate, true),
        subheader: `${startYmd} - ${endYmd}`,
      });
      return;
    }
    if (v === 'yearly') {
      const startDate = startOfYear(new Date(selectedYear, 0, 1));
      const endDate = endOfYear(new Date(selectedYear, 11, 31));
      const startYmd = getDateString(startDate);
      const endYmd = getDateString(endDate);

      setCustomRange({ start: startYmd, end: endYmd });
      emitDates({
        start: localDayBoundaryIso(startDate),
        end: localDayBoundaryIso(endDate, true),
        subheader: `01/01/${selectedYear} - 12/31/${selectedYear}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeType, customDays, selectedMonth, selectedYear]);

  useEffect(() => {
    applyRange();
  }, [applyRange]);

  useEffect(() => {
    if (!reset) return;
    setRangeType(options[0]);
    setDateType('30');
    setCustomDays('');
    setReset(false);
  }, [reset, setDateType, setReset]);

  const handleCustomChange = (field, value) => {
    const updated = { ...customRange, [field]: value };
    setCustomRange(updated);

    if (updated.start && updated.end) {
      emitDates({
        start: localDayBoundaryIso(ymdStringToLocalDate(updated.start)),
        end: localDayBoundaryIso(ymdStringToLocalDate(updated.end), true),
        subheader: `${updated.start} - ${updated.end}`,
      });
    }
  };

  // From: only the start date is user-picked — the end always tracks the
  // real current day (not `now`, which is yesterday in buttonView mode) so
  // today's still-in-progress data is included and grows on every save.
  const handleFromChange = (value) => {
    setCustomRange((prev) => ({ ...prev, start: value }));
    if (!value) return;

    const endYmd = getDateString(today);
    emitDates({
      start: localDayBoundaryIso(ymdStringToLocalDate(value)),
      end: localDayBoundaryIso(today, true),
      subheader: `${value} - ${endYmd}`,
    });
  };

  const handleRangeChange = (selectedOption) => {
    setDateType(selectedOption.value);
    setRangeType(selectedOption);

    if (selectedOption.value === 'monthly') {
      const d = getBaseDate(initialFrom, initialTo);

      setSelectedYear(d.getFullYear());
      setSelectedMonth(d.getMonth());
    }

    if (selectedOption.value === 'yearly') {
      const d = getBaseDate(initialFrom, initialTo);

      setSelectedYear(d.getFullYear());
    }

    if (selectedOption.value === 'custom') {
      setSubheader('Custom Range');
      setCustomRange({ start: '', end: '' });
    } else if (selectedOption.value === 'custom_days') {
      setTimeout(() => customRef.current?.focus?.(), 0);
    } else if (selectedOption.value === 'from') {
      handleFromChange(customRange?.start || getDateString(today));
    }
  };

  return (
    <div className={classNames([styles.container, className])}>
      {buttonView ? (
        <div className={styles.buttonView}>
          {buttonOptions.map((o) => {
            const isSelected =
              String(rangeType.value) === String(o.value) ||
              String(rangeType.value - 1) === String(o.value);

            return (
              <div
                key={o.label}
                onClick={() => handleRangeChange(o)}
                className={classNames([
                  styles.buttonSm,
                  isSelected && styles.selected,
                ])}
              >
                <Text
                  size="sm"
                  color={isSelected ? 'accentPrimary' : 'secondaryLight'}
                  noMargin
                  textAlign="center"
                  weight={isSelected ? 'semiBold' : 'regular'}
                  hover
                >
                  {o.label}
                </Text>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <Text
            noMargin
            weight={textProps?.weight || 'semiBold'}
            size={textProps?.size || 'md'}
          >
            Timeframe
          </Text>
          <Select
            value={rangeType}
            onChange={handleRangeChange}
            options={options}
            menuPortalTarget={document.body}
            styles={menuPortalStyles}
          />
        </>
      )}

      {rangeType.value === 'monthly' && (
        <div>
          {showTitle && (
            <Text
              noMargin
              weight={textProps?.weight || 'semiBold'}
              size={textProps?.size || 'md'}
            >
              Month and Year
            </Text>
          )}
          <div className={styles.monthSelector}>
            <Select
              value={months.find((month) => month.value === selectedMonth)}
              onChange={(option) => setSelectedMonth(option.value)}
              options={months}
              menuPortalTarget={document.body}
              styles={menuPortalStyles}
            />
            <Select
              value={years.find((year) => year.value === selectedYear)}
              onChange={(option) => setSelectedYear(option.value)}
              options={years}
              menuPortalTarget={document.body}
              styles={menuPortalStyles}
            />
          </div>
        </div>
      )}

      {rangeType.value === 'yearly' && (
        <Select
          value={years.find((year) => year.value === selectedYear)}
          onChange={(option) => setSelectedYear(option.value)}
          options={years}
          menuPortalTarget={document.body}
          styles={menuPortalStyles}
        />
      )}

      {rangeType.value === 'custom' && (
        <div>
          {showTitle && (
            <Text
              noMargin
              weight={textProps?.weight || 'semiBold'}
              size={textProps?.size || 'md'}
            >
              Dates
            </Text>
          )}
          <div className={styles.dateSelectors}>
            <input
              type="date"
              max={customRange?.start || now.toISOString().slice(0, 10)}
              className={
                !customRange?.start && customRange.end
                  ? styles.highlight
                  : styles.date
              }
              value={customRange?.start}
              onChange={(e) => handleCustomChange('start', e.target.value)}
            />
            <input
              type="date"
              max={now.toISOString().slice(0, 10)}
              min={customRange?.start || undefined}
              className={
                customRange?.start && !customRange.end
                  ? styles.highlight
                  : styles.date
              }
              value={customRange?.end}
              onChange={(e) => handleCustomChange('end', e.target.value)}
            />
          </div>
        </div>
      )}

      {rangeType.value === 'custom_days' && (
        <div className={styles.customDaysContainer}>
          <input
            ref={customRef}
            type="number"
            min={1}
            max={730}
            className={styles.numberInput}
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter custom days"
          />
        </div>
      )}

      {rangeType.value === 'from' && (
        <div className={styles.dateSelectors}>
          <input
            type="date"
            max={today.toISOString().slice(0, 10)}
            className={styles.date}
            value={customRange?.start || ''}
            onChange={(e) => handleFromChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
